import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Radio,
  FileText,
  Users,
  UserCircle,
  Megaphone,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Bell,
  Sun,
  Moon,
  Navigation,
  History,
  MapPin,
  Building,
  Car,
  RefreshCw,
  Image,
  Stamp,
  ClipboardList,
  Briefcase,
  Lock,
  Shield,
  ShieldAlert,
  Headphones,
  Phone,
  Eye,
  EyeOff,
  Monitor
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

// All available pages with their config (ordered as requested)
export const ALL_NAV_ITEMS = [
  { path: "/live-calls", icon: Radio, label: "Dispatch", page_key: "live_calls" },
  { path: "/incidents", icon: FileText, label: "Incidents", page_key: "incidents" },
  { path: "/investigations", icon: Briefcase, label: "Investigations", page_key: "investigations" },
  { path: "/response", icon: Navigation, label: "Response", page_key: "response" },
  { path: "/call-history", icon: History, label: "Call History", page_key: "call_history" },
  { path: "/patrol", icon: Car, label: "Patrol", page_key: "patrol" },
  { path: "/perpetrators", icon: UserCircle, label: "Profiles", page_key: "perpetrators" },
  { path: "/rollcall", icon: ClipboardList, label: "Roll Call", page_key: "rollcall" },
  { path: "/building-codes", icon: Building, label: "Building Codes", page_key: "building_codes" },
  { path: "/map", icon: MapPin, label: "Map", page_key: "map" },
  { path: "/notification-history", icon: Bell, label: "Notifications", page_key: "notification_history" },
  { path: "/media", icon: Image, label: "Media", page_key: "media" },
  { path: "/watermarking", icon: Stamp, label: "Watermarking", page_key: "watermarking" },
  { path: "/member-dashboard", icon: UserCircle, label: "My Dashboard", page_key: "member_dashboard" },
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", page_key: "dashboard" },
  { path: "/radio", icon: Headphones, label: "Radio", page_key: "radio" },
  { path: "/hotline", icon: Phone, label: "Hotline", page_key: "hotline" },
  { path: "/pr", icon: Megaphone, label: "PR", page_key: "pr" },
  { path: "/units", icon: Users, label: "Units", page_key: "units" },
  { path: "/event-ops", icon: Shield, label: "Event Ops", page_key: "event_ops" },
  { path: "/settings", icon: Settings, label: "Settings", page_key: "settings" }
];

// Navigation items based on user's page_access
export const getNavItems = (user) => {
  if (!user) return [];

  const userRole = user.role;
  const pageAccess = user.page_access || [];
  const permissions = user.permissions || [];

  // Admin gets everything
  if (userRole === "admin") {
    return ALL_NAV_ITEMS;
  }

  // If user has page_access set, use it
  if (pageAccess && pageAccess.length > 0) {
    let items = ALL_NAV_ITEMS.filter(item => pageAccess.includes(item.page_key));
    // Check rollcall_access permission for roll call page
    if (!user.rollcall_access && !pageAccess.includes("rollcall")) {
      items = items.filter(item => item.page_key !== "rollcall");
    }
    // Add rollcall if user has rollcall_access but it's not in page_access
    if (user.rollcall_access && !pageAccess.includes("rollcall")) {
      const rollcallItem = ALL_NAV_ITEMS.find(item => item.page_key === "rollcall");
      if (rollcallItem && !items.find(i => i.page_key === "rollcall")) {
        items.push(rollcallItem);
      }
    }
    // Add radio if user has recording_admin_access but it's not in page_access
    // temporarily disabled
    // if (user.recording_admin_access && !pageAccess.includes("radio")) {
    //   const radioItem = ALL_NAV_ITEMS.find(item => item.page_key === "radio");
    //   if (radioItem && !items.find(i => i.page_key === "radio")) {
    //     items.push(radioItem);
    //   }
    // }
    // Add hotline if user has hotline_access but it's not in page_access
    if (user.hotline_access && !pageAccess.includes("hotline")) {
      const hotlineItem = ALL_NAV_ITEMS.find(item => item.page_key === "hotline");
      if (hotlineItem && !items.find(i => i.page_key === "hotline")) {
        items.push(hotlineItem);
      }
    }
    // Tickets editors work in My Dashboard → Tickets, so they get My Dashboard.
    if (user.tickets_access && !pageAccess.includes("member_dashboard")) {
      const dashItem = ALL_NAV_ITEMS.find(item => item.page_key === "member_dashboard");
      if (dashItem && !items.find(i => i.page_key === "member_dashboard")) {
        items.push(dashItem);
      }
    }
    // Add investigations if user has investigations_access but it's not in page_access
    if (user.investigations_access && !pageAccess.includes("investigations")) {
      const invItem = ALL_NAV_ITEMS.find(item => item.page_key === "investigations");
      if (invItem && !items.find(i => i.page_key === "investigations")) {
        items.push(invItem);
      }
    }
    return items;
  }

  // Legacy role-based fallback for backwards compatibility
  const legacyRolePages = {
    "admin": ALL_NAV_ITEMS.filter(item => item.page_key !== "settings"),
    "superuser": ALL_NAV_ITEMS.filter(item => item.page_key !== "settings"),
    "operator": ALL_NAV_ITEMS.filter(item => ["live_calls", "incidents", "patrol", "patrol_admin"].includes(item.page_key)),
    "live_calls_only": ALL_NAV_ITEMS.filter(item => item.page_key === "live_calls"),
    "patrol": ALL_NAV_ITEMS.filter(item => ["patrol", "live_calls"].includes(item.page_key)),
    "response": ALL_NAV_ITEMS.filter(item => item.page_key === "response"),
    "response_history": ALL_NAV_ITEMS.filter(item => ["response", "call_history"].includes(item.page_key)),
    "history_only": ALL_NAV_ITEMS.filter(item => item.page_key === "call_history"),
  };

  let items = legacyRolePages[userRole] || [];
  // Add rollcall if user has rollcall_access
  if (user.rollcall_access) {
    const rollcallItem = ALL_NAV_ITEMS.find(item => item.page_key === "rollcall");
    if (rollcallItem && !items.find(i => i.page_key === "rollcall")) {
      items.push(rollcallItem);
    }
  }
  // Add radio if user has recording_admin_access
  // temporarily disabled
  // if (user.recording_admin_access) {
  //   const radioItem = ALL_NAV_ITEMS.find(item => item.page_key === "radio");
  //   if (radioItem && !items.find(i => i.page_key === "radio")) {
  //     items.push(radioItem);
  //   }
  // }
  // Add hotline if user has hotline_access
  if (user.hotline_access) {
    const hotlineItem = ALL_NAV_ITEMS.find(item => item.page_key === "hotline");
    if (hotlineItem && !items.find(i => i.page_key === "hotline")) {
      items.push(hotlineItem);
    }
  }
  // Add investigations if user has investigations_access
  if (user.investigations_access) {
    const invItem = ALL_NAV_ITEMS.find(item => item.page_key === "investigations");
    if (invItem && !items.find(i => i.page_key === "investigations")) {
      items.push(invItem);
    }
  }
  return items;
};

export default function Sidebar({
  user,
  onLogout,
  isDark,
  theme,
  onToggleTheme,
  cadMode = false,
  onToggleCadMode,
  notificationCount = 0,
  onNotificationClick,
  fontSize = 'normal',
  onCycleFontSize,
  appLockEnabled = false,
  phoneWidgetEnabled = false,
  phoneRegistered = false,
  phoneConnecting = false,
  phoneCallActive = false,
  onExpandDialer,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Measure mobile navbar height and set CSS variable for main content offset
  useEffect(() => {
    const measure = () => {
      const mobileNav = document.getElementById('mobile-navbar');
      const desktopHeader = document.getElementById('desktop-header');
      // Use whichever header is visible (mobile navbar or desktop header bar)
      const mobileH = mobileNav ? mobileNav.offsetHeight : 0;
      const desktopH = desktopHeader ? desktopHeader.offsetHeight : 0;
      const h = Math.max(mobileH, desktopH);
      document.documentElement.style.setProperty('--navbar-height', h + 'px');
    };
    // Measure immediately, after a short delay (for safe-area), and on resize
    measure();
    const timer = setTimeout(measure, 50);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => { clearTimeout(timer); window.removeEventListener('resize', measure); window.removeEventListener('orientationchange', measure); };
  }, []);

  // Get user's default homepage path
  const getHomePath = () => {
    const defaultPage = user?.default_homepage;
    if (defaultPage) {
      const navItem = ALL_NAV_ITEMS.find(item => item.page_key === defaultPage);
      if (navItem) return navItem.path;
    }
    // Fallback to first accessible page
    const navItems = getNavItems(user);
    return navItems.length > 0 ? navItems[0].path : "/dashboard";
  };
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const wakeLockRef = useRef(null);
  // Track user intent. Default ON — a dispatch console shouldn't let the screen
  // sleep — unless the user has explicitly turned it off before (persisted).
  const wakeLockWantedRef = useRef(
    (() => { try { return localStorage.getItem("screen_wake_lock") !== "off"; } catch { return true; } })()
  );
  const navRef = useRef(null);

  const acquireWakeLock = async () => {
    try {
      wakeLockRef.current = await navigator.wakeLock.request("screen");
      setWakeLockActive(true);
      wakeLockRef.current.addEventListener("release", () => {
        wakeLockRef.current = null;
        setWakeLockActive(false);
      });
    } catch {
      // Silently fail on re-acquire (e.g., low battery)
    }
  };

  const toggleWakeLock = async () => {
    if (wakeLockWantedRef.current) {
      wakeLockWantedRef.current = false;
      try { localStorage.setItem("screen_wake_lock", "off"); } catch {}
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
      setWakeLockActive(false);
      toast.success("Screen can turn off normally");
    } else {
      wakeLockWantedRef.current = true;
      try { localStorage.setItem("screen_wake_lock", "on"); } catch {}
      try {
        await acquireWakeLock();
        toast.success("Screen will stay on");
      } catch {
        wakeLockWantedRef.current = false;
        toast.error("Screen wake lock not supported on this device");
      }
    }
  };

  // Acquire on load when the user wants the screen kept on (the default).
  // Some browsers only grant wakeLock after a user gesture, so retry once on
  // the first interaction if the initial attempt didn't take.
  useEffect(() => {
    if (!("wakeLock" in navigator) || !wakeLockWantedRef.current) return;
    acquireWakeLock();
    const onFirstGesture = () => {
      if (wakeLockWantedRef.current && !wakeLockRef.current) acquireWakeLock();
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true });
    return () => window.removeEventListener("pointerdown", onFirstGesture);
  }, []);

  // Re-acquire wake lock when tab becomes visible again (if user wanted it on)
  useEffect(() => {
    const handleVisibility = async () => {
      if (wakeLockWantedRef.current && document.visibilityState === "visible" && !wakeLockRef.current) {
        await acquireWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLockApp = async () => {
    setLogoutLoading(true);
    try {
      await axios.post(`${API}/users/me/lock-app`, {}, { withCredentials: true });
      setShowLogoutDialog(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to lock app");
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleConfirmLogout = async () => {
    setLogoutLoading(true);
    try {
      // Unsubscribe from push notifications
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            await subscription.unsubscribe();
          }
        } catch (e) {
          console.error("Failed to unsubscribe from push manager:", e);
        }
        // Also tell backend to remove subscriptions
        try {
          await axios.post(`${API}/push/unsubscribe`, {}, { withCredentials: true });
        } catch (e) {
          console.error("Failed to unsubscribe from backend:", e);
        }
      }
      setShowLogoutDialog(false);
      onLogout();
    } finally {
      setLogoutLoading(false);
    }
  };

  const navItems = useMemo(() => getNavItems(user), [user?.user_id, user?.page_access?.length, user?.role]);

  // Check scroll position for arrows - stable callback
  const checkScrollPosition = () => {
    const el = navRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;
    // Can scroll up if not at top
    setCanScrollUp(scrollTop > 5);
    // Can scroll down if not at bottom (with 5px threshold)
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 5);
  };

  // Setup scroll listener - runs once on mount
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    // Initial check
    checkScrollPosition();
    // Listen for scroll
    el.addEventListener('scroll', checkScrollPosition, { passive: true });
    // Also check on resize
    window.addEventListener('resize', checkScrollPosition);
    return () => {
      el.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, []);

  // Re-check scroll when nav items change (user permissions update)
  useEffect(() => {
    setTimeout(checkScrollPosition, 100);
  }, [navItems.length]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Restore nav scroll position after route change
  useEffect(() => {
    const savedScrollPos = sessionStorage.getItem('sidebarScrollPos');
    if (savedScrollPos && navRef.current) {
      navRef.current.scrollTop = parseInt(savedScrollPos, 10);
    }
  }, [location.pathname]);

  // Save scroll position before navigation
  const saveScrollPosition = () => {
    if (navRef.current) {
      sessionStorage.setItem('sidebarScrollPos', navRef.current.scrollTop.toString());
    }
  };

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const NavLink = ({ item }) => {
    // Use exact match for most paths, or startsWith for paths that have sub-routes
    const isActive = item.path === "/" 
      ? location.pathname === "/" 
      : location.pathname === item.path || location.pathname.startsWith(item.path + "/");
    return (
      <Link
        to={item.path}
        preventScrollReset={true}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => {
          // Save sidebar scroll position before navigation
          saveScrollPosition();
          // Close mobile menu when navigating
          if (isMobileOpen) {
            setIsMobileOpen(false);
          }
        }}
        className={`
          flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 select-none
          ${isActive
            ? "bg-blue-600/20 text-blue-600 dark:text-blue-400 border-l-4 border-blue-500"
            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-200 border-l-4 border-transparent"
          }
          ${isCollapsed && !isMobileOpen ? "justify-center px-2" : ""}
        `}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        title={isCollapsed ? item.label : undefined}
      >
        <item.icon className={`w-5 h-5 flex-shrink-0 pointer-events-none ${isActive ? "text-blue-600 dark:text-blue-400" : ""}`} />
        {(!isCollapsed || isMobileOpen) && (
          <span className="font-medium truncate pointer-events-none">{item.label}</span>
        )}
      </Link>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo/Header */}
      <div className={`p-4 border-b border-slate-200 dark:border-slate-800 ${isCollapsed && !isMobileOpen ? "px-2" : ""}`}>
        <div className="flex items-center justify-between">
          {(!isCollapsed || isMobileOpen) && (
            <div className="flex items-center gap-2">
              <img
                src="/CHSP_PR_2026.png"
                alt="CHSP"
                className="w-8 h-8 object-contain pointer-events-none select-none"
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
              />
              <h1 className="text-xl font-bold tracking-tight text-blue-600 dark:text-blue-400 uppercase select-none">
                CHSP
              </h1>
            </div>
          )}
          {isCollapsed && !isMobileOpen && (
            <img 
              src="/CHSP_PR_2026.png" 
              alt="CHSP" 
              className="w-8 h-8 object-contain mx-auto pointer-events-none select-none" 
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
          {/* Desktop collapse toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden md:flex p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          {/* Mobile close */}
          <button
            onClick={() => setIsMobileOpen(false)}
            className="md:hidden p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Scroll up indicator */}
        {canScrollUp && (
          <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-white dark:from-slate-900 to-transparent z-10 flex items-start justify-center pointer-events-none">
            <ChevronUp className="w-4 h-4 text-slate-400 dark:text-slate-500 animate-bounce" />
          </div>
        )}
        <nav ref={navRef} className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
        </nav>
        {/* Scroll down indicator */}
        {canScrollDown && (
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-slate-900 to-transparent z-10 flex items-end justify-center pointer-events-none">
            <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500 animate-bounce" />
          </div>
        )}
      </div>

      {/* Bottom Section */}
      <div className={`p-3 border-t border-slate-200 dark:border-slate-800 space-y-2 ${isCollapsed && !isMobileOpen ? "px-2" : ""}`}>
        {/* Refresh, Font Size & Notification Row */}
        <div className={`flex gap-1 ${isCollapsed && !isMobileOpen ? "flex-col" : ""}`}>
          {/* Refresh Button */}
          <button
            onClick={() => window.location.reload()}
            className={`
              flex items-center justify-center px-3 py-2.5 rounded-lg text-slate-500 dark:text-slate-400
              hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200 transition-colors
            `}
            title="Refresh page"
          >
            <RefreshCw className="w-5 h-5 flex-shrink-0" />
          </button>

          {/* Font Size Toggle Button - only show if user has permission */}
          {user?.allow_text_size_toggle !== false && (
            <button
              onClick={onCycleFontSize}
              className={`
                flex items-center justify-center px-3 py-2.5 rounded-lg text-slate-500 dark:text-slate-400
                hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200 transition-colors
              `}
              title={`Text size: ${fontSize} (click to cycle)`}
            >
              <span className="w-5 h-5 flex items-center justify-center font-bold text-sm">
                A<span className="text-[10px] ml-0.5">{fontSize === 'small' ? 'S' : fontSize === 'normal' ? 'M' : fontSize === 'large' ? 'L' : 'XL'}</span>
              </span>
            </button>
          )}

          {/* Notification Button */}
          <button
            onClick={onNotificationClick}
            className={`
              flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-500 dark:text-slate-400
              hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200 transition-colors relative
              ${isCollapsed && !isMobileOpen ? "justify-center px-2" : ""}
            `}
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5 flex-shrink-0" />
            {(!isCollapsed || isMobileOpen) && <span>Notifications</span>}
            {notificationCount > 0 && (
              <span className={`
                absolute bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px]
                flex items-center justify-center
                ${isCollapsed && !isMobileOpen ? "top-1 right-1" : "top-1.5 right-2"}
              `}>
                {notificationCount > 99 ? "99+" : notificationCount}
              </span>
            )}
          </button>
        </div>

        {/* Theme + Screen On (side by side) */}
        <div className={`flex ${isCollapsed && !isMobileOpen ? "flex-col gap-1" : "gap-1"}`}>
          <button
            onClick={onToggleTheme}
            className={`
              flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-slate-500 dark:text-slate-400
              hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200 transition-colors
              ${isCollapsed && !isMobileOpen ? "justify-center px-2" : ""}
            `}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4 flex-shrink-0" /> : theme === 'light' ? <Shield className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
            {(!isCollapsed || isMobileOpen) && <span className="text-sm">{theme === 'dark' ? 'Light' : theme === 'light' ? 'Blue' : 'Dark'}</span>}
          </button>
          {onToggleCadMode && (
            <button
              onClick={onToggleCadMode}
              className={`
                flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${cadMode
                  ? "text-amber-400 bg-amber-500/10 hover:bg-amber-500/20"
                  : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200"
                }
                ${isCollapsed && !isMobileOpen ? "justify-center px-2" : ""}
              `}
              title="Toggle CAD Mode"
            >
              <Monitor className="w-4 h-4 flex-shrink-0" />
            </button>
          )}
          {"wakeLock" in navigator && (
            <button
              onClick={toggleWakeLock}
              className={`
                flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${wakeLockActive
                  ? "text-amber-400 bg-amber-500/10 hover:bg-amber-500/20"
                  : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200"
                }
                ${isCollapsed && !isMobileOpen ? "justify-center px-2" : ""}
              `}
            >
              {wakeLockActive ? <Eye className="w-4 h-4 flex-shrink-0" /> : <EyeOff className="w-4 h-4 flex-shrink-0" />}
              {(!isCollapsed || isMobileOpen) && <span className="text-sm">{wakeLockActive ? "Screen On" : "Screen Off"}</span>}
            </button>
          )}
        </div>

        {/* User Info + Dispatch Phone */}
        <div className={`flex items-center gap-2 ${isCollapsed && !isMobileOpen ? "flex-col" : ""}`}>
          {(!isCollapsed || isMobileOpen) && (
            <div className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800/30">
              <p className="text-sm text-slate-700 dark:text-slate-300 truncate">{user?.name}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400 uppercase">{user?.role?.replace("_", " ")}</p>
            </div>
          )}
          {phoneWidgetEnabled && (
            <button
              onClick={() => { onExpandDialer(); if (isMobileOpen) setIsMobileOpen(false); }}
              className={`
                relative flex items-center justify-center rounded-lg transition-all
                ${phoneCallActive
                  ? "bg-green-600/20 text-green-400 hover:bg-green-600/30 animate-pulse"
                  : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200"
                }
                ${isCollapsed && !isMobileOpen ? "w-10 h-10" : "w-10 h-10 flex-shrink-0"}
              `}
              title={phoneCallActive ? "On Call — tap to show" : "Dispatch Phone"}
            >
              <Phone className="w-5 h-5" />
              <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                phoneRegistered ? "bg-green-500" : phoneConnecting ? "bg-yellow-500 animate-pulse" : "bg-red-500"
              }`} />
            </button>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogoutClick}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-500 dark:text-slate-400
            hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors
            ${isCollapsed && !isMobileOpen ? "justify-center px-2" : ""}
          `}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {(!isCollapsed || isMobileOpen) && <span>Logout</span>}
        </button>
      </div>

      {/* Logout Confirmation Dialog */}
      {showLogoutDialog && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !logoutLoading && setShowLogoutDialog(false)}>
          <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">What would you like to do?</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {appLockEnabled
                  ? "You can lock the app or log out completely."
                  : "Are you sure you want to log out?"
                }
              </p>
            </div>

            <div className="space-y-2">
              {appLockEnabled && (
                <button
                  onClick={handleLockApp}
                  disabled={logoutLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 font-medium transition-colors disabled:opacity-50"
                >
                  <Lock className="w-4 h-4" />
                  Lock App
                </button>
              )}

              <button
                onClick={handleConfirmLogout}
                disabled={logoutLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 font-medium transition-colors disabled:opacity-50"
              >
                <LogOut className="w-4 h-4" />
                {logoutLoading ? "Logging out..." : "Log Out"}
              </button>

              <button
                onClick={() => setShowLogoutDialog(false)}
                disabled={logoutLoading}
                className="w-full px-4 py-2.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

            {!appLockEnabled && (
              <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center">
                Logging out will also unsubscribe you from notifications
              </p>
            )}
            {appLockEnabled && (
              <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center">
                Lock App keeps you logged in. Log Out will unsubscribe from notifications.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Header Bar - with iOS safe area padding */}
      <div
        id="mobile-navbar"
        className="md:hidden fixed top-0 left-0 right-0 z-[100] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-2"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: '8px',
          minHeight: '56px'
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMobileOpen(true);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            setIsMobileOpen(true);
          }}
          className="p-3 -m-1 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white active:bg-slate-200 dark:active:bg-slate-700 rounded-lg select-none z-10"
          data-testid="mobile-menu-btn"
          style={{
            minWidth: '48px',
            minHeight: '48px',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation'
          }}
        >
          <Menu className="w-7 h-7 pointer-events-none" />
        </button>
        {/* Centered logo — this IS the home button, so the header doesn't spend
            a second slot on one. */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(getHomePath()); }}
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 select-none"
          style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          title="Home"
        >
          <img
            src="/CHSP_PR_2026.png"
            alt="CHSP"
            className="w-7 h-7 object-contain pointer-events-none select-none"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
          <h1 className="text-lg font-bold tracking-tight text-blue-600 dark:text-blue-400 uppercase select-none">
            CHSP
          </h1>
        </button>
        <div className="flex items-center z-10">
          {/* Refresh Button */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.reload();
            }}
            className="p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white active:bg-slate-200 dark:active:bg-slate-700 rounded-lg select-none"
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5 pointer-events-none" />
          </button>
        </div>
      </div>

      {/* Desktop Header Bar - logo centered */}
      <div
        id="desktop-header"
        className={`
          hidden md:flex fixed top-0 z-[90] h-12 items-center justify-center
          bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800
          transition-all duration-300
          ${isCollapsed ? "left-16" : "left-64"}
          right-0
        `}
      >
        <button
          type="button"
          onClick={() => navigate(getHomePath())}
          className="flex items-center gap-2 select-none cursor-pointer"
          title="Home"
        >
          <img
            src="/CHSP_PR_2026.png"
            alt="CHSP"
            className="w-7 h-7 object-contain pointer-events-none select-none"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
          <h1 className="text-lg font-bold tracking-tight text-blue-600 dark:text-blue-400 uppercase select-none">
            CHSP
          </h1>
        </button>
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-[110] backdrop-blur-sm"
          onClick={() => setIsMobileOpen(false)}
          onTouchEnd={(e) => {
            e.preventDefault();
            setIsMobileOpen(false);
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white/95 dark:bg-slate-900/95 border-r border-slate-200 dark:border-slate-800 z-[120]
          transition-all duration-300 ease-in-out
          ${isMobileOpen ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0"}
          ${isCollapsed && !isMobileOpen ? "md:w-16" : "md:w-64"}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Spacer for content */}
      <div className={`
        hidden md:block flex-shrink-0 transition-all duration-300
        ${isCollapsed ? "w-16" : "w-64"}
      `} />
    </>
  );
}
